import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { AppState } from "@excalidraw/excalidraw/types";

export interface TemplateMeta {
  id: string;
  title: string;
  description: string;
  image: string;
  category?: string;
  popular?: boolean;
  new?: boolean;
  stars?: number;
}

export const getTemplates = async (): Promise<TemplateMeta[]> => {
  if (typeof window !== "undefined") {
    const res = await fetch("/api/templates");
    if (!res.ok) {
      throw new Error("api error");
    }
    return (await res.json()) as TemplateMeta[];
  }

  const { promises: fs } = await import("fs");
  const path = await import("path");
  const indexPath = path.join(
    process.cwd(),
    "public",
    "templates",
    "index.json",
  );
  const raw = await fs.readFile(indexPath, "utf8");
  return JSON.parse(raw) as TemplateMeta[];
};

export const saveTemplate = async ({
  id,
  elements,
  appState,
  thumbnail,
  meta,
}: {
  id: string;
  elements: readonly ExcalidrawElement[];
  appState: AppState;
  thumbnail: string;
  meta: Omit<TemplateMeta, "id" | "image">;
}) => {
  if (typeof window !== "undefined") {
    const res = await fetch("/api/save-template", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, elements, appState, thumbnail, meta }),
    });
    if (!res.ok) {
      throw new Error("api error");
    }
    return;
  }

  const { promises: fs } = await import("fs");
  const path = await import("path");
  const dir = path.join(process.cwd(), "public", "templates");
  await fs.mkdir(dir, { recursive: true });
  await fs.writeFile(
    path.join(dir, `${id}.json`),
    JSON.stringify({ elements, appState }, null, 2),
    "utf8",
  );
  const buffer = Buffer.from(
    thumbnail.replace(/^data:image\/png;base64,/, ""),
    "base64",
  );
  await fs.writeFile(path.join(dir, `${id}.png`), buffer);
  await updateIndex({ id, image: `/templates/${id}.png`, ...meta });
};

export const updateIndex = async (entry: TemplateMeta) => {
  const { promises: fs } = await import("fs");
  const path = await import("path");
  const indexPath = path.join(
    process.cwd(),
    "public",
    "templates",
    "index.json",
  );
  let list: TemplateMeta[] = [];
  try {
    const raw = await fs.readFile(indexPath, "utf8");
    list = JSON.parse(raw) as TemplateMeta[];
  } catch {}
  const idx = list.findIndex((e) => e.id === entry.id);
  if (idx >= 0) {
    list[idx] = entry;
  } else {
    list.push(entry);
  }
  await fs.writeFile(indexPath, JSON.stringify(list, null, 2), "utf8");
};
